import { CURRENCIES } from "@garimpa/lib/currencies";

/**
 * Ambiguous dollar-sign currencies that may need disambiguation
 * when displayed alongside each other.
 */
const AMBIGUOUS_DOLLAR_CURRENCIES = new Set(["USD"]);

/**
 * Format a numeric price with the appropriate currency symbol and locale formatting.
 *
 * Uses narrowSymbol to keep "$" compact (not "US$"), strips trailing zeros
 * so R$230 is shown instead of R$230.00.
 */
export function formatPrice(amount: string | number, currency: string, locale?: string): string {
  const value = typeof amount === "string" ? Number(amount) : amount;

  return new Intl.NumberFormat(locale, {
    style: "currency",
    currency,
    currencyDisplay: "narrowSymbol",
    minimumFractionDigits: 0,
    maximumFractionDigits: 2,
    trailingZeroDisplay: "stripIfInteger",
  }).format(value);
}

/**
 * Returns true for currencies that share the ambiguous "$" symbol
 * and may need additional context (e.g., "USD" label) to distinguish.
 */
export function needsDisambiguation(currency: string): boolean {
  return AMBIGUOUS_DOLLAR_CURRENCIES.has(currency.toUpperCase());
}

/**
 * Look up the display name for a currency code from the CURRENCIES list.
 * Returns the currency code itself as a fallback if not found.
 */
export function getCurrencyDisplayName(currency: string): string {
  const entry = CURRENCIES.find((c) => c.code === currency.toUpperCase());
  return entry?.name ?? currency;
}
